import { CartDeviceAdvertisement } from '../models/Checkout/CartDeviceAdvertisement'
import { Totals } from '../models/Checkout/Totals'
import { AdDesignPrice, AdSelectionTypes, ApprovalTypes } from './constants'
import { getCostFromDateRange } from './dateUtils'

export interface CouponTotals {
  discountedTotals: Totals
  couponDiscount: number
  discountedBundleTotal: number
}

export function applyCouponDiscount(
  totals: Totals,
  cartItems: CartDeviceAdvertisement[],
  bundleTotal: number,
  hasCoupon: boolean,
): CouponTotals {
  if (!hasCoupon) {
    return { discountedTotals: totals, couponDiscount: 0, discountedBundleTotal: bundleTotal }
  }
  const itemCouponDiscount = cartItems
    .filter((item) => item.numberOfDaysToRunAd === 30)
    .reduce((sum, item) => sum + getCostForItem(item), 0)
  const couponDiscount = itemCouponDiscount + bundleTotal
  const discountedTotals: Totals = {
    totalAmount: Math.max(0, totals.totalAmount - itemCouponDiscount),
    totalDueNow: Math.max(0, totals.totalDueNow - itemCouponDiscount),
    totalDueAfterApproval: Math.max(0, totals.totalDueAfterApproval - itemCouponDiscount),
  }
  return { discountedTotals, couponDiscount, discountedBundleTotal: 0 }
}

export const getCostForItem = (item: CartDeviceAdvertisement) => {
  // Determine the appropriate price based on the selected time period
  let cost = 0

  if (item.numberOfDaysToRunAd) {
    // Use predefined time periods
    switch (item.numberOfDaysToRunAd) {
      case 7: // 1 week
        cost = item.device.weeklyPrice
        break
      case 14: // 2 weeks
        cost = item.device.weeklyPrice * 2
        break
      case 30: // 1 month
        cost = item.device.monthlyPrice
        break
      case 90: // 3 months
        cost = item.device.monthlyPrice * 3
        break
      default:
        cost = item.device.monthlyPrice
        break
    }
  } else {
    // Custom date range - use daily price calculation
    cost = getCostFromDateRange(
      item.device.dailyPrice,
      [item.startDateLocal, item.endDateLocal],
      item.businessHours,
      item.numberOfDaysToRunAd,
      item.device.weeklyPrice,
      item.device.monthlyPrice,
    )
  }

  return cost
}

export function calculateTotals(cartItems: CartDeviceAdvertisement[]): Totals {
  return cartItems.reduce(
    (totals, item) => {
      const itemCost = getCostForItem(item)

      totals.totalAmount += itemCost

      if (item.device.approvalType === ApprovalTypes.Auto) {
        totals.totalDueNow += itemCost
      } else {
        totals.totalDueAfterApproval += itemCost
      }

      if (
        item.advertisement.adSelectionType === AdSelectionTypes.PayForDesign
      ) {
        totals.totalAmount += AdDesignPrice
        totals.totalDueNow += AdDesignPrice
      }

      return totals
    },
    { totalAmount: 0, totalDueNow: 0, totalDueAfterApproval: 0 }, // Initial object with zeroed totals
  )
}
